import React from "react";
import { Box } from "@mui/material";
import { DotLegalHeader, DotLegalTableWithControls, GridSizes, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";

export const managementReportFilterGridSizes: GridSizes = { xs: 12, sm: 6, md: 4, lg: 3 };

export interface ManagementReportTableSectionProps<T> {
    title: string;
    count: number;
    isPdf: boolean;
    headers: Array<ITableHeader<T>>;
    defaultOrderBy: keyof T;
    data: Array<T>;
    searchFields?: JSX.Element;
    headerMarginBottom?: number;
    renderRow: (row: T) => React.ReactElement;
}

// The report is one long printable page, so every section hides itself when empty, never paginates, and drops its
// filter controls in the PDF rendering where they cannot be operated.
function ManagementReportTableSection<T>(props: ManagementReportTableSectionProps<T>) {
    const { translateString } = useTranslation();

    if (props.data.length === 0) {
        return undefined;
    }

    return (
        <Box sx={{ marginTop: 3 }} className="avoid-page-break">
            <DotLegalHeader headerStyle={"medium"} marginBottom={props.headerMarginBottom}>
                {props.title.toUpperCase()}{" "}
                <Box sx={(theme) => ({ color: theme.palette.grey[500] })} component="span">
                    ({props.count})
                </Box>
            </DotLegalHeader>
            <DotLegalTableWithControls
                getUserSpecificPageLength={() => 1000}
                extraControls={props.isPdf ? undefined : props.searchFields}
                labelRowsPerPage={""}
                labelSearch={translateString("search")}
                noOptionsLabel={translateString("noOptions")}
                headers={props.headers}
                defaultOrderBy={props.defaultOrderBy}
                defaultOrder={"asc"}
                paginationLabelOf={""}
                hidePagination
                hideRowsPerPage
                clickableRows={false}
                data={props.data}
                emptyText={translateString("noData")}
                renderRow={props.renderRow}
            />
        </Box>
    );
}

export default ManagementReportTableSection;
